import { useSelector } from 'react-redux'

import { selectors } from 'store/modules/user-session'
import { MESSAGE_MANAGEMENT, QLE } from 'routes/path-names'
import {
  Census,
  CompanyAcctSettingPNG,
  ManageEmployeePNG,
  Message,
  ReportsPNG,
  QLEImagePNG,
  EnrollmentDashboard,
} from '../../../assets'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { selectors as authSelectors } from '@hixme/auth-ui'
import {
  isDemoAllowedNavHref,
  isDemoHrAdminRestricted,
} from 'enrollment-platform/demo-restriction'
import { useStableUsRestrictedDataAccess } from 'enrollment-platform/rbac/useStableUsRestrictedDataAccess'

// Tile hrefs that expose US-restricted member data and are gated on
// Manage UsRestrictedData.
const US_RESTRICTED_TILE_HREFS = ['/enrollmentdashboard', '/dataandreports']

export default function useTiles() {
  const companyProfileUndefined = useSelector(
    selectors.isCurrentCompanyUnavailable
  )
  const role = useSelector(authSelectors.getRole)
  const currentCompany = useSelector(selectors.getCurrentCompanyNew)
  const demoRestricted = isDemoHrAdminRestricted(role, currentCompany)

  const { flags } = useFeatureFlags()
  const showEnrollmentDashboard = flags['admin.show-enrollment-dashboard']
  const showReportsTab = flags['admin.show-reports-tab-to-hr-users']
  const showQLE = flags['qle-v-1']

  // Same check as RegionRestrictedDataBanner, so the tiles are hidden whenever
  // the banner shows. Ignores the RBAC kill-switch and fails closed while the
  // ability loads.
  const { hasUsRestrictedDataAccess: showUsRestrictedTiles } =
    useStableUsRestrictedDataAccess()

  const tiles = [
    {
      href: '/employees',
      image: ManageEmployeePNG,
      title: 'Our People',
      line1: 'Search and manage all of your',
      line2: 'company employees in a list.',
    },
    {
      href: '/enrollmentdashboard',
      image: EnrollmentDashboard,
      title: 'Enrollment Dashboard',
      line1: 'View the progress',
      line2: 'of your enrollment.',
      visibleForRoles: {
        platformHixmeAdmin: showEnrollmentDashboard,
        platformClientAdmin: showEnrollmentDashboard,
        platformBroker: showEnrollmentDashboard,
      },
    },
    {
      href: MESSAGE_MANAGEMENT,
      image: Message,
      title: 'Message Management',
      line1: 'Review and decision incoming',
      line2: 'employee messages.',
      visibleForRoles: { platformHixmeAdmin: true },
    },
    {
      href: '/census-management',
      image: Census,
      title: 'Census Management',
      line1: 'Send and view census files of your',
      line2: 'employees.',
    },
    {
      href: '/dataandreports',
      image: ReportsPNG,
      title: 'Reports Requests',
      line1: 'View the breakdown',
      line2: 'of your company data.',
      visibleForRoles: {
        platformHixmeAdmin: true,
        platformBroker: true,
        platformClientAdmin: showReportsTab,
      },
    },
    {
      href: '/company',
      image: CompanyAcctSettingPNG,
      title: 'Company Profile',
      line1: 'Manage your company settings',
      line2: 'and admins.',
      visibleForRoles: {
        platformHixmeAdmin: true,
        platformClientAdmin: true,
        platformBroker: true,
      },
      disabled: companyProfileUndefined,
    },
    {
      href: `/${QLE}`,
      image: QLEImagePNG,
      title: 'Qualifying Life Events',
      line1: 'Manage qualifying life',
      line2: 'events.',
      visibleForRoles: {
        platformHixmeAdmin: showQLE,
      },
    },
  ]

  // Gate Enrollment Dashboard and Data & Reports on US-restricted data access —
  // same subject as the sidebar nav entries and the routes, so all surfaces
  // stay in sync.
  const gated = showUsRestrictedTiles
    ? tiles
    : tiles.filter((tile) => !US_RESTRICTED_TILE_HREFS.includes(tile.href))

  // A demo company's HR admin sees only the allow-listed surfaces on Home too,
  // matching the sidebar restriction.
  if (demoRestricted) {
    return gated.filter((tile) => isDemoAllowedNavHref(tile.href))
  }

  return gated
}
